import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import pluralize from 'pluralize'
import queryString from 'query-string'

import { formatDuration } from '@/utils/helpers'
import { Table } from '@/components/tables/Table/Table'
import { Drawer } from '@/components/drawer/Drawer'
import { CopyButton } from '@/components/buttons/CopyButton'
import { ExternalLink } from '@/components/ExternalLink'
import { BaltoGPTChat } from '@/components/baltoGPT/BaltoGPTChat'
import { CogitationOrb } from '@/components/ai/CogitationOrb'
import { searchTrendsWithBaltoGPT } from '@/reducers/baltoGPT/baltoGPT.actions'
import { getTrendsSuggestions } from '@/components/baltoGPT/baltoGPT.helpers'

export const TrendEvidenceDrawer = ({ graphClickData, onClose, organizationId }) => {
  const dispatch = useDispatch()
  const { organizationid } = useSelector((state) => state.currentUser)
  const organizations = useSelector((state) => state.organizations)
  const currentOrganization = useSelector((state) => state.currentOrganization)
  const isBaltoAdmin = organizationid === 1

  const hasCallSummarization = isBaltoAdmin
    ? organizations?.find((org) => org.id === organizationId)?.call_summarization_enabled
    : currentOrganization.call_summarization_enabled

  if (!graphClickData?.samples) {
    return null
  }

  const callIds = graphClickData.samples.map((sample) => sample.call_id)
  const suggestions = getTrendsSuggestions()

  const getCallExplorerLink = () => {
    const params = queryString.stringify({
      call_ids: callIds,
      organizationId,
    })

    return `/explorer?${params}`
  }

  const handleChatSubmit = (values, signal) => {
    dispatch(
      searchTrendsWithBaltoGPT(
        {
          ...values,
          call_ids: callIds,
          trend_context: {
            trend_question: graphClickData.trendQuestion || '',
            trend_name: graphClickData.trendName || '',
            category_name: graphClickData.name || '',
            category_description: graphClickData.description || '',
            precondition_statements: graphClickData.preconditionStatements || [],
          },
        },
        signal
      )
    )
  }

  return (
    <Drawer
      id="trends-evidence-drawer"
      wide
      title={
        hasCallSummarization ? `BaltoGPT Session: ${graphClickData.name}` : graphClickData.name
      }
      autoTrigger
      onClose={onClose}
    >
      <div className="trends-evidence-container">
        {hasCallSummarization && (
          <div className="flex-align-center">
            <CogitationOrb resting />
            <div className="ml balto-gpt-label">BaltoGPT</div>
          </div>
        )}
        <div className="data-container">
          <p>{graphClickData.description}</p>
          <Table className="call-data-table">
            <thead>
              <tr>
                <th>Call ID</th>
                <th>Duration</th>
                <th>Agent</th>
              </tr>
            </thead>
            <tbody>
              {graphClickData.samples.slice(0, 5).map((call) => (
                <tr key={call.call_id}>
                  <td className="flex-align-center medium-gap">
                    <a
                      href={`/calls/${call.call_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="blurple-link"
                    >
                      {call.call_id}
                    </a>
                    <CopyButton iconOnly basic content={call.call_id} />
                  </td>
                  <td>{formatDuration(call.duration_s)}</td>
                  <td>{call.agent_name}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3}>
                  <div className="flex-space-between flex-align-center">
                    <span className="muted-text ml trends-call-count">
                      {`${graphClickData.samples.length} ${pluralize('Call', graphClickData.samples.length)}`}
                    </span>
                    <div className="flex-align-center">
                      <ExternalLink
                        className="trends-view-calls-btn"
                        url={getCallExplorerLink()}
                        target="_blank"
                        label="View Calls"
                      />
                    </div>
                  </div>
                </td>
              </tr>
            </tfoot>
          </Table>
        </div>
        {hasCallSummarization && (
          <div className="trends-chat-container">
            <BaltoGPTChat
              useMinimalUi
              channel="call"
              callIds={callIds}
              onSubmit={handleChatSubmit}
              suggestions={suggestions}
            />
          </div>
        )}
      </div>
    </Drawer>
  )
}
